import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Flag, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI } from '../../services/api';
import { useMobile } from '../../hooks/useMobile';

const REPORT_REASON_OPTIONS = [
  { value: 'spam', label: 'Spam', description: 'Promotional or repetitive content.' },
  { value: 'inappropriate_content', label: 'Inappropriate content', description: 'Offensive, sexual, or disturbing material.' },
  { value: 'harassment', label: 'Harassment', description: 'Bullying, threats, or hateful content.' },
  { value: 'misleading_or_fraudulent', label: 'Misleading or fraudulent', description: 'Scams, impersonation, or deceptive claims.' },
  { value: 'other', label: 'Other', description: 'Something else that should be reviewed.' },
];

const INITIAL_FORM = {
  reason: '',
  details: '',
};

const ProjectReportModal = ({ isOpen, onClose, projectId, projectName }) => {
  const { isMobile } = useMobile();
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const closeTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const detailsFieldRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const submitReportMutation = useMutation({
    mutationFn: (payload) => projectAPI.report(projectId, payload),
  });

  const selectedReason = useMemo(
    () => REPORT_REASON_OPTIONS.find((option) => option.value === form.reason) || null,
    [form.reason]
  );

  const handleClose = () => {
    if (submitReportMutation.isPending) return;
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.reason) {
      setError('Please choose a reason before sending your report.');
      return;
    }

    setError('');

    try {
      await submitReportMutation.mutateAsync({
        reason: form.reason,
        details: form.details.trim() || null,
      });

      setShowSuccess(true);
      toast.success('Report submitted');
      closeTimeoutRef.current = window.setTimeout(() => {
        closeTimeoutRef.current = null;
        onClose?.();
      }, 1200);
    } catch (submitError) {
      const detail =
        submitError?.response?.data?.detail ||
        submitError?.message ||
        'Could not send your report.';
      setError(detail);
    }
  };

  const ensureDetailsVisible = () => {
    if (!isMobile) return;

    window.setTimeout(() => {
      try {
        detailsFieldRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } catch (_) {}
    }, 250);
  };

  const modalVariants = {
    hidden: isMobile ? { y: '100%', opacity: 0 } : { y: 16, opacity: 0, scale: 0.97 },
    visible: { y: 0, opacity: 1, scale: 1 },
    exit: isMobile ? { y: '100%', opacity: 0 } : { y: 16, opacity: 0, scale: 0.97 },
  };

  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 z-[50002] bg-black/50 ${
            isMobile ? 'flex items-end justify-center' : 'flex items-center justify-center p-4'
          }`}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.18 }}
          onClick={handleClose}
          role="dialog"
          aria-modal="true"
          aria-label="Report project"
        >
          <motion.div
            className={`w-full bg-white dark:bg-gray-800 flex flex-col ${
              isMobile ? 'rounded-t-3xl' : 'max-w-lg rounded-3xl shadow-2xl'
            }`}
            style={
              isMobile
                ? {
                    height: '88dvh',
                    maxHeight: '88dvh',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                  }
                : undefined
            }
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: isMobile ? 0.28 : 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Report project</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Help us review content that may violate the community rules.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitReportMutation.isPending}
                className="rounded-full p-2 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700"
                aria-label="Close report modal"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-300" />
              </button>
            </div>

            {showSuccess ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center overflow-y-auto">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Report sent</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Thanks. The admins can review this project now.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  className="flex-1 space-y-5 overflow-y-auto px-5 py-5 mobile-scroll"
                  style={{
                    paddingBottom: isMobile
                      ? 'calc(env(safe-area-inset-bottom, 0px) + 24px)'
                      : '24px',
                    overscrollBehavior: 'contain',
                  }}
                >
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/30">
                    <div className="flex items-start gap-3">
                      <Flag className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <div>
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                          Reporting `{projectName || 'this project'}`
                        </p>
                        <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                          Include a short note if there is context the admins should know.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="project-report-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Reason
                    </label>
                    <select
                      id="project-report-reason"
                      value={form.reason}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, reason: event.target.value }));
                        setError('');
                      }}
                      onFocus={ensureDetailsVisible}
                      className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                      required
                    >
                      <option value="">Select a reason</option>
                      {REPORT_REASON_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {selectedReason && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{selectedReason.description}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="project-report-details" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Details
                    </label>
                    <textarea
                      ref={detailsFieldRef}
                      id="project-report-details"
                      value={form.details}
                      onChange={(event) => setForm((current) => ({ ...current, details: event.target.value }))}
                      onFocus={ensureDetailsVisible}
                      rows={5}
                      maxLength={2000}
                      placeholder="Add anything helpful for the admins to review."
                      className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    />
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Optional</span>
                      <span>{form.details.length}/2000</span>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-200 px-5 pt-4 pb-5 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitReportMutation.isPending}
                    className="rounded-2xl px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitReportMutation.isPending || !form.reason}
                    className="rounded-2xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitReportMutation.isPending ? 'Sending...' : 'Send report'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ProjectReportModal;
